import { makeObservable, observable, computed, action } from 'mobx'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'

/**
 * Class representing a Comment.
 */
/**
 * Comment represents a user comment with metadata and attachments.
 *
 * @class Comment
 * @param {Object} params - Initialization parameters.
 * @param {string} [params.id] - Unique identifier for the comment. Generated if absent.
 * @param {string} params.description - The content of the comment.
 * @param {number} [params.sortId=0] - Ordering index for the comment.
 * @param {string} [params.web=""] - Optional related URL.
 * @param {string} params.createdBy - User ID of the comment author.
 * @param {number} [params.createdTs] - Unix timestamp of creation. Defaults to now.
 * @param {Array<string>} [params.images=[]] - Filenames of uploaded images.
 */
export class Comment {
	id
	description
	sortId
	createdTs
	createdBy
	images = []
	web

	constructor({
		id,
		description,
		sortId,
		web,
		createdBy,
		createdTs,
		images = []
	}) {
		this.id = id || uuidv4()
		this.description = description
		this.sortId = sortId || 0
		this.web = web || ''
		this.createdBy = createdBy
		this.createdTs = createdTs || dayjs().unix()
		this.images = images || []

		makeObservable(this, {
			description: observable,
			sortId: observable,
			web: observable,
			createdTs: observable,
			createdBy: observable,
			images: observable,
			delete: action.bound,
			addImage: action.bound,
			removeImage: action.bound,
			setWeb: action.bound
		})
	}

	/**
	 * Placeholder for deletion logic. Actual removal handled by parent.
	 */
	delete() {
		// Logic to delete this comment can be handled by the parent collection (Comments)
		// Here we just define the method for structure completeness
	}

	/**
	 * Adds an image filename to this comment.
	 *
	 * @param {string} fileName - Name of the image file to add.
	 */
	addImage(fileName) {
		this.images.push(fileName)
	}

	/**
	 * Removes an image filename from this comment.
	 *
	 * @param {string} fileName - Name of the image file to remove.
	 */
	removeImage(fileName) {
		this.images = this.images.filter((image) => image !== fileName)
	}

	/**
	 * Updates the related web link for this comment.
	 *
	 * @param {string} web - New URL to associate.
	 */
	setWeb(web) {
		this.web = web
	}
}

/**
 * Class representing a collection of Comments.
 */
/**
 * Comments manages a collection of Comment instances.
 *
 * @class Comments
 * @param {Array<Object>} [list=[]] - Initial array of comment data objects.
 */
export class Comments {
	list = []

	constructor(list = []) {
		this.list = Array.isArray(list)
			? list.map((comment) => new Comment(comment))
			: []
		makeObservable(this, {
			list: observable,
			addComment: action.bound,
			deleteComment: action.bound,
			getCommentById: action.bound,
			sortedList: computed,
			commentCount: computed
		})
	}

	/**
	 * Returns comments sorted by their sortId in ascending order.
	 *
	 * @type {Array<Comment>}
	 */
	get sortedList() {
		return [...this.list].sort((a, b) => a.sortId - b.sortId)
	}

	/**
	 * Prepares raw data for persistence by returning the list array.
	 *
	 * @returns {Array<Comment>}
	 */
	prepareForSave() {
		if (!Array.isArray(this.list)) {
			return [] // Ensure the function returns an empty array if this.list is not an array
		}

		return this.list.map((comment) => comment)
	}

	/**
	 * Updates an existing comment in the collection.
	 *
	 * @param {Comment} updatedComment - The comment instance with updated fields.
	 */
	updateComment(updatedComment) {
		const index = this.list.findIndex(
			(comment) => comment.id === updatedComment.id
		)
		if (index !== -1) {
			this.list[index] = updatedComment
		}
	}

	/**
	 * Creates and appends a new comment.
	 *
	 * @param {string} description - Content of the new comment.
	 * @param {number} sortId - Ordering index.
	 * @param {string} web - Optional URL.
	 * @param {Array<string>} images - Filenames of uploaded images.
	 * @param {string} createdBy - User ID of the author.
	 */
	addComment(description, sortId, web, images, createdBy) {
		const comment = new Comment({
			description,
			sortId,
			web,
			images,
			createdBy
		})
		this.list.push(comment)
	}

	/**
	 * Removes a comment from the collection by its ID.
	 *
	 * @param {string} commentId - Identifier of the comment to delete.
	 */
	deleteComment(commentId) {
		this.list = this.list.filter((comment) => comment.id !== commentId)
	}

	/**
	 * Retrieves a comment instance by its ID.
	 *
	 * @param {string} commentId - Identifier of the desired comment.
	 * @returns {Comment|undefined} The matching comment or undefined if not found.
	 */
	getCommentById(commentId) {
		return this.list.find((comment) => comment.id === commentId)
	}

	/**
	 * Total number of comments in the collection.
	 *
	 * @type {number}
	 */
	get commentCount() {
		return this.list.length
	}
}
